import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { api, extractErrorMessage } from '../../api/client';
import { useToast } from '../../components/Toast';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { Confirm } from '../../components/Confirm';
import { MenuImportar } from '../../components/MenuImportar';
import { BuscadorSku, type SkuSugerido } from '../../components/BuscadorSku';
import { ETIQUETAS_STOCK, formatoStock } from '../../components/StockSku';
import { Obligatorio } from '../../components/Obligatorio';
import { useHayCambios } from '../../hooks/useHayCambios';
import { useCierreConfirmado } from '../../hooks/useCierreConfirmado';
import { useCamposObligatorios, faltaSi } from '../../hooks/useCamposObligatorios';
import { referenciaLegible, type OrigenReferencia } from '../../lib/referenciaMovimiento';
import { MAX_DIAS_ATRAS_HECHO, aLocalAr, ahoraLocalAr, fechaHoraAr, localArAIso, minHechoLocalAr } from '../../lib/fechaHoraAr';
import { etiquetaTipoMovimiento } from '../../lib/tiposMovimiento';
import { SincronizarSkusConteo } from './SincronizarSkusConteo';
import type { VariantesDeSku } from '../../lib/variantes';

/**
 * Detalle de un conteo de inventario: carga de líneas (buscador de SKU simples + cantidad), importación desde planilla,
 * diferencias contra el sistema (si el conteo no es ciego o quien mira es el responsable), confirmar y anular. El sistema
 * es el stock a la hora del conteo ("Contado el", que se cambia en borrador, o la hora de carga de cada línea); cada
 * línea muestra lo que entró o salió de la ubicación después de esa hora (sigue aplicado).
 * Un conteo a medio confirmar muestra qué renglones faltan ajustar y el responsable puede excluir uno pendiente (un SKU
 * que falla siempre) para terminarlo. Si confirmar o importar no responde (red, timeout del proxy) o falla el servidor,
 * se recarga el conteo y se avisa que pudo haberse aplicado igual, con su estado real.
 * "Por contar": SKU sugeridos (los pausados por el stock de resguardo, o el de "Contar este SKU") que todavía no tienen
 * línea; tocar uno lo pone en el buscador. Es sólo una ayuda: no se guarda en el conteo.
 * "Empezar a sincronizar estos SKU" (confirmado; admin con catalogo:editar, como "Empezar a sincronizar"): enciende la
 * sincronización con Mercado Libre de los SKU del conteo, de a uno (ver SincronizarSkusConteo).
 * "Contar por color" (opcional por línea, si el SKU tiene variantes activas): un campo por color más "Sin identificar";
 * el total es la suma y ajusta el stock como siempre. Al confirmar, cada color contado queda en lo contado y Sin asignar
 * absorbe el resto; los colores que no se cargan no se tocan. Corregir una línea con desglose la vuelve a cargar acá.
 */

export type TipoConteo = 'inicial' | 'ciclico';
export type EstadoConteo = 'borrador' | 'confirmando' | 'confirmado' | 'anulado';

export const URL_CONTEOS = '/api/v3/conteos-inventario';
export const TIPO_CONTEO: Record<TipoConteo, string> = { inicial: 'Inventario inicial', ciclico: 'Cíclico' };
export const ESTADO_CONTEO: Record<EstadoConteo, { etiqueta: string; tono: 'amber' | 'green' | 'slate' }> = {
  borrador: { etiqueta: 'Borrador', tono: 'amber' },
  confirmando: { etiqueta: 'A medio confirmar', tono: 'amber' },
  confirmado: { etiqueta: 'Confirmado', tono: 'green' },
  anulado: { etiqueta: 'Anulado', tono: 'slate' },
};

/** Día de una columna de fecha (medianoche UTC): se muestra sin correrse por la zona horaria. */
export const fechaCorta = (v: string | null) => {
  if (!v) return '—';
  const [a, m, d] = v.slice(0, 10).split('-');
  return d && m && a ? `${d}/${m}/${a}` : v;
};

interface LineaConteo {
  id: number;
  sku: string;
  descripcion: string | null;
  contado: number;
  stockSistema: number | null;
  diferencia: number | null;
  ajusteAplicado: number | null;
  /** Stock reservado y disponible del SKU en todo el depósito (null en un conteo ciego, como el sistema). */
  reservado?: number | null;
  disponible?: number | null;
  /** El SKU tiene unidades en cuarentena (están en el depósito: se cuentan); cuántas, null en un conteo ciego. */
  enCuarentena?: boolean;
  cuarentena?: number | null;
  contadoPor: string | null;
  contadoEn: string;
  /** Hora a la que se toma el sistema ("Contado el" o contadoEn). */
  referenciaEn?: string;
  /** Lo que entró o salió de la ubicación después de esa hora; null si no se ve el sistema. */
  movimientosPosteriores?: MovimientosPosteriores | null;
  /** No se ajusta: ya hay un conteo posterior del SKU en la ubicación ("Conteo #N" o "Conteo de estante"). */
  superadaPor?: string | null;
  /** Desglose por color (vacío o ausente: la línea es sólo el total); sistema null en un conteo ciego. */
  variantes?: Array<{ varianteId: number; nombre: string; colorHex: string | null; contado: number; sistema: number | null }>;
  /** Contado que no se pudo asignar a un color (null sin desglose). */
  sinIdentificar?: number | null;
}

interface MovimientosPosteriores {
  cantidad: number;
  neto: number;
  ultimos: Array<{ tipo: string; cantidad: number; momento: string; referencia: string | null; origenReferencia?: OrigenReferencia | null }>;
}

interface ResumenConteo {
  lineas: number;
  positivas: number | null;
  negativas: number | null;
  sinDiferencia: number | null;
  unidadesPositivas: number | null;
  unidadesNegativas: number | null;
  /** Borrador: líneas que quedarían en negativo al confirmar (se vendió más de lo contado). */
  enNegativo: number | null;
  /** Superadas por un conteo posterior (no se ajustan). */
  superadas?: number | null;
}


interface Detalle {
  id: number;
  fecha: string;
  tipo: TipoConteo;
  ubicacionCodigo: string | null;
  ciego: boolean;
  estado: EstadoConteo;
  notas: string | null;
  /** Cuándo se contó, si fue antes de cargarlo; null: cada línea usa su hora de carga. */
  contadoEl?: string | null;
  creadoPor: string | null;
  confirmadoPor: string | null;
  confirmadoEn: string | null;
  anuladoPor: string | null;
  anuladoEn: string | null;
  sistemaVisible: boolean;
  lineas: LineaConteo[];
  resumen: ResumenConteo;
}

interface ErrorFila { fila: number; sku: string | null; mensaje: string }
interface ResultadoImportacion { importadas: number; nuevas: number; actualizadas: number; errores: ErrorFila[] }
interface ResultadoConfirmacion {
  id: number;
  lineas: number;
  positivas: number;
  negativas: number;
  sinDiferencia: number;
  /** Quedaron en negativo en la ubicación: después de la hora del conteo se vendió más de lo contado. */
  enNegativo: Array<{ sku: string; queda: number }>;
  superadas?: Array<{ sku: string; ubicacion: string | null; por: string }>;
  reservasLiberadas: string[];
  /** SKU cuya cuarentena bajó: el conteo encontró en el depósito menos de lo que figuraba en cuarentena. */
  cuarentenaAjustada?: Array<{ sku: string; antes: number; queda: number }>;
  /** SKU cuyos colores quedaron por encima de lo que hay para repartir (físico − cuarentena). */
  coloresSobreasignados?: string[];
  /** Líneas por color cuyos colores no se (o no todos se) aplicaron, con el motivo. */
  coloresNoAplicados?: Array<{ sku: string; motivo: string }>;
  /** Líneas que no se ajustaron (p. ej. el SKU pasó a ser pack/combo después de cargarlas), con el motivo. */
  noAplicadas?: Array<{ sku: string; motivo: string }>;
  resincronizacionPendiente: boolean;
  aviso: string | null;
}

const cantidad = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 4 });
/** −N con el signo menos tipográfico (como en los motivos del backend). */
const conSignoMenos = (n: number) => (n < 0 ? `−${cantidad(-n)}` : cantidad(n));

/**
 * Sin respuesta (error de red, el proxy cortó por tiempo) o error del servidor (5xx): la operación pudo haberse aplicado
 * igual en el backend. Un 4xx es una respuesta del backend (no se aplicó o se explica en el mensaje).
 */
export const resultadoIncierto = (e: unknown): boolean => {
  const status = (e as { status?: unknown } | null)?.status;
  return typeof status !== 'number' || status >= 500;
};

interface AvisoIncierto {
  accion: 'confirmar' | 'importar';
  mensaje: string;
  estado: EstadoConteo | null;
  lineas: number | null;
}
/** SKU simples (sin packs/combos) desde conteos-inventario/skus: pide wms:ver y no trae costos ni precios. */
const buscarSkus = (texto: string) => api.get<SkuSugerido[]>(`${URL_CONTEOS}/skus?q=${encodeURIComponent(texto)}`);

/** Diferencia con color y texto (no sólo color): hay más o menos de lo que figuraba, o coincide. */
function Diferencia({ valor }: { valor: number }) {
  if (valor > 0) return <span className="text-green-700 font-medium">{`+${cantidad(valor)} · hay más de lo que figuraba`}</span>;
  if (valor < 0) return <span className="text-red-700 font-medium">{`−${cantidad(-valor)} · hay menos de lo que figuraba`}</span>;
  return <span className="text-slate-500">0 (coincide)</span>;
}

/** Con signo: +3, −5, 0. */
const conSigno = (n: number) => (n > 0 ? `+${cantidad(n)}` : n < 0 ? `−${cantidad(-n)}` : '0');

/** Lo que entró o salió de la ubicación después de la hora del conteo: neto (cantidad), con la lista al desplegarlo. */
function MovimientosDesdeConteo({ m }: { m: MovimientosPosteriores | null | undefined }) {
  if (!m) return <span className="text-slate-400">—</span>;
  if (m.cantidad === 0) return <span className="text-slate-500">Sin movimientos</span>;
  return (
    <details className="text-left inline-block">
      <summary className="cursor-pointer whitespace-nowrap">{`${conSigno(m.neto)} (${m.cantidad})`}</summary>
      <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
        {m.ultimos.map((x, i) => (
          <li key={`${x.momento}-${i}`} className="whitespace-nowrap">
            {`${fechaHoraAr(x.momento)} · ${etiquetaTipoMovimiento(x.tipo)} · ${conSigno(x.cantidad)}${x.referencia ? ` · ${referenciaLegible(x.referencia, x.origenReferencia).texto}` : ''}`}
          </li>
        ))}
        {m.cantidad > m.ultimos.length && <li>{`y ${m.cantidad - m.ultimos.length} más (ver Movimientos de stock)`}</li>}
      </ul>
    </details>
  );
}

const formLineaVacio = () => ({
  sku: '', cantidad: '', porColor: false, colores: {} as Record<number, string>, sinIdentificar: '',
});
type FormLinea = ReturnType<typeof formLineaVacio>;
/** Cambia el SKU del formulario: el desglose por color es del SKU anterior, se descarta. */
const conSku = (f: FormLinea, sku: string): FormLinea => ({ ...f, sku, porColor: false, colores: {}, sinIdentificar: '' });
const numeroOVacio = (v: string) => (v.trim() === '' ? null : Number(v));

/** Columna de acciones fija a la derecha: se ve aunque la tabla no entre en el modal y se desplace a lo ancho. */
const COLUMNA_ACCIONES = 'sticky right-0 bg-white pl-2';

export function DetalleConteoModal({ id, puedeCargar, esResponsable, puedeSincronizar = false, porContar, onClose, onCambio }: {
  id: number;
  puedeCargar: boolean;
  esResponsable: boolean;
  /** Puede empezar a sincronizar SKU con Mercado Libre (admin con catalogo:editar, como en Sincronizar con ML). */
  puedeSincronizar?: boolean;
  /** SKU sugeridos para contar; los que ya tienen línea no se muestran. */
  porContar?: string[];
  onClose: () => void;
  /** El conteo cambió (líneas, estado): la lista se recarga. */
  onCambio: () => void;
}) {
  const { addToast } = useToast();
  const idSku = useId();
  const [detalle, setDetalle] = useState<Detalle | null>(null);
  /** No se pudo cargar al abrir (p. ej. un enlace a un conteo que no existe): se avisa en vez de quedar "Cargando…". */
  const [errorCarga, setErrorCarga] = useState('');
  const [form, setForm] = useState(formLineaVacio());
  const [editando, setEditando] = useState<{ id: number; cantidad: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [importando, setImportando] = useState(false);
  const [erroresImportacion, setErroresImportacion] = useState<ErrorFila[] | null>(null);
  const [preguntar, setPreguntar] = useState<null | 'confirmar' | 'anular' | { quitar: LineaConteo } | { excluir: LineaConteo }>(null);
  const [incierto, setIncierto] = useState<AvisoIncierto | null>(null);
  /** "Contado el" en edición (valor del datetime-local; vacío = a la hora de carga de cada línea). */
  const [contadoElEditado, setContadoElEditado] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  /** Variantes del SKU que se está por cargar (para «Contar por color»); se piden al escribirlo, sin esperar al Agregar. */
  const [variantesSku, setVariantesSku] = useState<VariantesDeSku | null>(null);
  /** Aviso de colores de la línea cargada que ya no están activos (su cantidad pasó a sin identificar). */
  const [avisoInactivos, setAvisoInactivos] = useState('');

  const cierre = useCierreConfirmado(useHayCambios({ form, editando, contadoElEditado }, true), onClose);
  const obligatorios = useCamposObligatorios(true);

  const cargar = useCallback(async (): Promise<Detalle | null> => {
    try {
      const d = await api.get<Detalle>(`${URL_CONTEOS}/${id}`);
      setDetalle(d);
      setErrorCarga('');
      return d;
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
      setErrorCarga(extractErrorMessage(e));
      return null;
    }
  }, [id, addToast]);

  useEffect(() => { void cargar(); }, [cargar]);

  const skuForm = form.sku.trim();
  const formPorColor = useRef(false);
  formPorColor.current = form.porColor;
  useEffect(() => {
    let vigente = true;
    setVariantesSku(null);
    if (!skuForm) return undefined;
    const t = setTimeout(() => {
      api.get<VariantesDeSku>(`/api/v3/catalogo/${encodeURIComponent(skuForm)}/variantes`)
        .then((v) => { if (vigente && v?.variantes) setVariantesSku(v); })
        .catch((e) => {
          if (!vigente) return;
          // Sin colores se carga sólo el total; si la línea venía por color, se avisa en vez de quedar esperando.
          setForm((f) => (f.porColor ? { ...f, porColor: false } : f));
          if (formPorColor.current) addToast(`No se pudieron cargar los colores de ${skuForm}: ${extractErrorMessage(e)}`, 'error');
        });
    }, 250);
    return () => { vigente = false; clearTimeout(t); };
  }, [skuForm]);
  const coloresActivos = variantesSku && variantesSku.skuInterno === skuForm ? variantesSku.variantes.filter((v) => v.activo) : [];
  const cargandoColores = form.porColor && !!skuForm && coloresActivos.length === 0 && !(variantesSku && variantesSku.skuInterno === skuForm);
  // Una línea cargada con un color que después se desactivó: su cantidad pasa a sin identificar (el total no cambia).
  useEffect(() => {
    if (!form.porColor || coloresActivos.length === 0) return;
    const activos = new Set(coloresActivos.map((v) => v.id));
    const inactivos = Object.entries(form.colores).filter(([id, v]) => !activos.has(Number(id)) && numeroOVacio(v) !== null);
    if (inactivos.length === 0) return;
    const movido = inactivos.reduce((t, [, v]) => t + (numeroOVacio(v) ?? 0), 0);
    const nombres = inactivos.map(([id]) => variantesSku?.variantes.find((x) => x.id === Number(id))?.nombre ?? `variante ${id}`);
    setAvisoInactivos(`${nombres.join(', ')} ${nombres.length === 1 ? 'está inactivo' : 'están inactivos'}: ${nombres.length === 1 ? 'sus' : 'sus'} ${cantidad(movido)} unidades pasaron a sin identificar (el total no cambia).`);
    setForm((f) => ({
      ...f,
      colores: Object.fromEntries(Object.entries(f.colores).filter(([id]) => activos.has(Number(id)))),
      sinIdentificar: String((numeroOVacio(f.sinIdentificar) ?? 0) + movido),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantesSku]);
  const totalPorColor = Object.values(form.colores).reduce((t, v) => t + (numeroOVacio(v) ?? 0), 0) + (numeroOVacio(form.sinIdentificar) ?? 0);

  const editable = detalle?.estado === 'borrador' && puedeCargar;
  const visible = !!detalle?.sistemaVisible;
  const confirmado = detalle?.estado === 'confirmado';
  /** A medio confirmar: el responsable puede excluir un renglón pendiente para terminarlo. */
  const puedeExcluir = detalle?.estado === 'confirmando' && esResponsable;
  const conAjuste = confirmado || detalle?.estado === 'confirmando';
  /**
   * SKU del conteo, sin repetir y en el orden de las líneas. Memorizado por contenido: volver a dibujarse (o recargar el
   * conteo con las mismas líneas) no le pasa otra lista a la ventana de sincronizar, que si no recargaría su vista previa.
   */
  const claveSkus = detalle ? [...new Set(detalle.lineas.map((l) => l.sku))].join('\n') : '';
  const skusDelConteo = useMemo(() => (claveSkus ? claveSkus.split('\n') : []), [claveSkus]);
  const puedeEmpezarSync = confirmado && puedeSincronizar && skusDelConteo.length > 0;
  const faltanContar = editable && detalle
    ? (porContar ?? []).filter((sku) => !detalle.lineas.some((l) => l.sku === sku))
    : [];

  const elegirPorContar = (sku: string) => {
    setForm((f) => conSku(f, sku));
    document.getElementById(`${idSku}-cantidad`)?.focus();
  };

  /** Corregir una línea con desglose por color: se vuelve a cargar en el formulario (guardar la reemplaza). */
  const cargarEnFormulario = (l: LineaConteo) => {
    setAvisoInactivos('');
    setForm({
      sku: l.sku, cantidad: String(l.contado), porColor: true,
      colores: Object.fromEntries((l.variantes ?? []).map((v) => [v.varianteId, String(v.contado)])),
      sinIdentificar: l.sinIdentificar ? String(l.sinIdentificar) : '',
    });
    document.getElementById(idSku)?.focus();
  };

  const trasCambio = async () => {
    await cargar();
    onCambio();
  };

  /** Confirmar o importar sin respuesta o con 5xx: recarga el conteo (la lista también) y muestra su estado real. */
  const avisarIncierto = async (accion: AvisoIncierto['accion'], e: unknown) => {
    const d = await cargar();
    onCambio();
    setIncierto({ accion, mensaje: extractErrorMessage(e), estado: d?.estado ?? null, lineas: d ? d.lineas.length : null });
  };

  const agregar = async () => {
    if (ocupado || !obligatorios.validar()) return;
    // Por color: sólo viajan los colores con cantidad cargada (los demás no se tocan al confirmar).
    const porColor = form.porColor && coloresActivos.length > 0;
    const colores = porColor
      ? coloresActivos.flatMap((v) => { const n = numeroOVacio(form.colores[v.id] ?? ''); return n === null ? [] : [{ varianteId: v.id, contado: n }]; })
      : [];
    if (porColor && colores.length === 0 && numeroOVacio(form.sinIdentificar) === null) {
      addToast('Cargá la cantidad contada de al menos un color', 'error');
      return;
    }
    const total = porColor ? totalPorColor : Number(form.cantidad);
    const cuerpo = colores.length > 0
      ? { sku: form.sku.trim(), variantes: colores, sinIdentificar: numeroOVacio(form.sinIdentificar) ?? 0 }
      : { sku: form.sku.trim(), contado: total };
    setOcupado(true);
    try {
      await api.post(`${URL_CONTEOS}/${id}/lineas`, cuerpo);
      addToast(`${form.sku.trim()}: ${cantidad(total)} contados`, 'success');
      setForm(formLineaVacio());
      document.getElementById(idSku)?.focus();
      await trasCambio();
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setOcupado(false);
    }
  };

  const guardarEdicion = async () => {
    if (!editando || ocupado) return;
    const n = Number(editando.cantidad);
    if (editando.cantidad.trim() === '' || !Number.isFinite(n) || n < 0) {
      addToast('Cargá una cantidad de 0 o más', 'error');
      return;
    }
    setOcupado(true);
    try {
      await api.patch(`${URL_CONTEOS}/${id}/lineas/${editando.id}`, { contado: n });
      setEditando(null);
      await trasCambio();
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setOcupado(false);
    }
  };

  const guardarContadoEl = async () => {
    if (contadoElEditado === null || ocupado) return;
    setOcupado(true);
    try {
      await api.patch(`${URL_CONTEOS}/${id}`, { contadoEl: contadoElEditado ? localArAIso(contadoElEditado) : null });
      setContadoElEditado(null);
      await trasCambio();
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setOcupado(false);
    }
  };

  const importar = async (archivo: File) => {
    setImportando(true);
    setErroresImportacion(null);
    setIncierto(null);
    try {
      const r = await api.upload<ResultadoImportacion>(`${URL_CONTEOS}/${id}/importar`, archivo);
      if (r.errores.length > 0) {
        setErroresImportacion(r.errores);
      } else {
        addToast(`Se importaron ${r.importadas} líneas (${r.nuevas} nuevas, ${r.actualizadas} corregidas)`, 'success');
        await trasCambio();
      }
    } catch (e) {
      if (resultadoIncierto(e)) await avisarIncierto('importar', e);
      else addToast(extractErrorMessage(e), 'error');
    } finally {
      setImportando(false);
    }
  };

  const responder = async () => {
    const accion = preguntar;
    setPreguntar(null);
    if (!accion) return;
    setOcupado(true);
    setIncierto(null);
    try {
      if (accion === 'confirmar') {
        const r = await api.post<ResultadoConfirmacion>(`${URL_CONTEOS}/${id}/confirmar`, {});
        const cuarentena = r.cuarentenaAjustada?.length
          ? ` · Cuarentena ajustada: ${r.cuarentenaAjustada.map((c) => `${c.sku} ${cantidad(c.antes)}→${cantidad(c.queda)}`).join(', ')}`
          : '';
        addToast(`Conteo #${id} confirmado: ${r.positivas} SKU suman stock, ${r.negativas} restan, ${r.sinDiferencia} sin cambios${cuarentena}`, 'success');
        if (r.enNegativo.length > 0) {
          addToast(r.enNegativo.map((x) => `${x.sku} queda en ${conSignoMenos(x.queda)}`).join(', ') + ': se vendió más de lo contado', 'info', 10000);
        }
        if (r.superadas && r.superadas.length > 0) {
          addToast(`No se ajustaron porque ya había un conteo posterior: ${r.superadas.map((x) => `${x.sku} (${x.por})`).join(', ')}`, 'info', 10000);
        }
        if (r.coloresSobreasignados?.length) {
          addToast(`Colores por encima de lo que hay para repartir: ${r.coloresSobreasignados.join(', ')}. Revisalos con «Reasignar variante» (Sin asignar quedó negativo).`, 'info', 10000);
        }
        if (r.coloresNoAplicados?.length) addToast(`Colores sin aplicar: ${r.coloresNoAplicados.map((x) => x.motivo).join('; ')}`, 'info', 10000);
        if (r.noAplicadas?.length) addToast(`Líneas sin ajustar: ${r.noAplicadas.map((x) => x.motivo).join('; ')}`, 'info', 10000);
        if (r.reservasLiberadas.length > 0) addToast(`Stock de resguardo liberado: ${r.reservasLiberadas.join(', ')}`, 'info');
        if (r.aviso) addToast(r.aviso, 'error', 10000);
      } else if (accion === 'anular') {
        await api.post(`${URL_CONTEOS}/${id}/anular`, {});
        addToast(`Conteo #${id} anulado`, 'success');
      } else if ('excluir' in accion) {
        await api.post(`${URL_CONTEOS}/${id}/lineas/${accion.excluir.id}/excluir`, {});
        addToast(`Se excluyó ${accion.excluir.sku}: volvé a confirmar el conteo para terminarlo`, 'success');
      } else {
        await api.delete(`${URL_CONTEOS}/${id}/lineas/${accion.quitar.id}`);
        addToast(`Se quitó ${accion.quitar.sku}`, 'success');
      }
      await trasCambio();
    } catch (e) {
      if (accion === 'confirmar' && resultadoIncierto(e)) {
        await avisarIncierto('confirmar', e);
      } else {
        addToast(extractErrorMessage(e), 'error');
        if (accion === 'confirmar') void trasCambio(); // pudo quedar a medio confirmar
      }
    } finally {
      setOcupado(false);
    }
  };

  const r = detalle?.resumen;
  const mensajeConfirmar = r ? [
    `Líneas: ${r.lineas}`,
    `Se suman al stock: ${r.positivas ?? 0} SKU${r.unidadesPositivas ? ` · +${cantidad(r.unidadesPositivas)} u.` : ''}`,
    `Se restan del stock: ${r.negativas ?? 0} SKU${r.unidadesNegativas ? ` · −${cantidad(r.unidadesNegativas)} u.` : ''}`,
    `Sin diferencia: ${r.sinDiferencia ?? 0}`,
    `Quedarían en negativo (se vendió más de lo contado): ${r.enNegativo ?? 0}`,
    ...(r.superadas ? [`Superadas por un conteo posterior (no se ajustan): ${r.superadas}`] : []),
    '',
    'Ajusta el depósito con la diferencia de cada SKU contra el stock a la hora del conteo (lo que salió o entró después sigue aplicado) y actualiza Mercado Libre. No se puede deshacer.',
  ].join('\n') : '';

  const titulo = detalle ? `Conteo #${id} · ${TIPO_CONTEO[detalle.tipo]}` : `Conteo #${id}`;

  return (
    <Modal open cierre={cierre} title={titulo} size="full">
      {!detalle && errorCarga ? (
        <div className="space-y-3">
          <p role="alert" className="text-sm text-red-600">{`No se pudo abrir el conteo #${id}: ${errorCarga}`}</p>
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
        </div>
      ) : !detalle ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <Badge tone={ESTADO_CONTEO[detalle.estado].tono}>{ESTADO_CONTEO[detalle.estado].etiqueta}</Badge>
            {detalle.ciego && <Badge tone="slate">Ciego</Badge>}
            <span>Fecha: {fechaCorta(detalle.fecha)}</span>
            <span>· Ubicación: {detalle.ubicacionCodigo ?? '—'}</span>
            <span>· Creado por {detalle.creadoPor ?? '—'}</span>
            {detalle.confirmadoEn && <span>· Confirmado por {detalle.confirmadoPor ?? '—'} el {fechaHoraAr(detalle.confirmadoEn)}</span>}
            {detalle.anuladoEn && <span>· Anulado por {detalle.anuladoPor ?? '—'} el {fechaHoraAr(detalle.anuladoEn)}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            {contadoElEditado === null ? (
              <>
                <span>
                  Contado el: {detalle.contadoEl ? fechaHoraAr(detalle.contadoEl) : 'a la hora de carga de cada línea'}
                </span>
                {editable && (
                  <Button size="sm" variant="ghost" aria-label="Cambiar Contado el"
                    onClick={() => setContadoElEditado(detalle.contadoEl ? aLocalAr(detalle.contadoEl) : ahoraLocalAr())}>
                    Cambiar
                  </Button>
                )}
              </>
            ) : (
              <>
                <label htmlFor={`${idSku}-contado-el`}>Contado el</label>
                <input
                  id={`${idSku}-contado-el`}
                  type="datetime-local"
                  aria-label="Contado el"
                  className="px-2 py-1 border rounded"
                  value={contadoElEditado}
                  min={minHechoLocalAr()}
                  max={ahoraLocalAr()}
                  onChange={(e) => setContadoElEditado(e.target.value)}
                />
                <Button size="sm" aria-label="Guardar Contado el" onClick={() => void guardarContadoEl()} loading={ocupado}>Guardar</Button>
                <Button size="sm" variant="ghost" onClick={() => setContadoElEditado(null)}>Cancelar</Button>
                <span className="text-xs text-slate-500">Hora de Argentina, hasta {MAX_DIAS_ATRAS_HECHO} días atrás. Vacío: cada línea usa su hora de carga.</span>
              </>
            )}
          </div>
          {detalle.notas && <p className="text-sm text-slate-600 whitespace-pre-line">{detalle.notas}</p>}

          {incierto && (
            <div role="status" aria-label="Resultado incierto" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">
                {incierto.accion === 'confirmar' ? 'La confirmación' : 'La importación'} no terminó de responder ({incierto.mensaje}): puede haberse aplicado igual: revisá el estado.
              </p>
              <p>
                {incierto.estado ? `Estado actual: ${ESTADO_CONTEO[incierto.estado].etiqueta}` : 'No se pudo recargar el conteo'}
                {incierto.lineas !== null ? `, ${incierto.lineas} ${incierto.lineas === 1 ? 'línea' : 'líneas'}. ` : '. '}
                {incierto.accion === 'confirmar'
                  ? 'Si quedó a medio confirmar, volvé a confirmarlo para terminarlo (no repite lo ya ajustado).'
                  : 'La importación es todo o nada: si las líneas de la planilla ya están, no hace falta volver a importarla.'}
              </p>
            </div>
          )}

          {faltanContar.length > 0 && (
            <section aria-labelledby={`${idSku}-por-contar`} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
              <h3 id={`${idSku}-por-contar`} className="font-medium text-amber-900">Por contar</h3>
              <p className="text-xs text-amber-900 mb-2">
                {faltanContar.length === 1 ? 'Falta cargar 1 SKU' : `Faltan cargar ${faltanContar.length} SKU`}: tocá uno para cargar lo contado.
              </p>
              <div className="flex flex-wrap gap-2">
                {faltanContar.map((sku) => (
                  <Button key={sku} size="sm" variant="ghost" className="bg-white font-mono" aria-label={`Contar ${sku}`} onClick={() => elegirPorContar(sku)}>
                    {sku}
                  </Button>
                ))}
              </div>
            </section>
          )}

          {editable && (
            <div className="flex flex-wrap items-end gap-3">
              <div {...obligatorios.contenedor} className="flex flex-wrap items-end gap-3 flex-1">
                <div className="flex-1 min-w-[16rem]">
                  <label htmlFor={idSku} className="block text-sm font-medium mb-1">SKU<Obligatorio /></label>
                  <BuscadorSku
                    id={idSku}
                    aria-label="SKU"
                    placeholder="Escribí el SKU o la descripción"
                    {...faltaSi(!form.sku.trim())}
                    value={form.sku}
                    onChange={(texto) => setForm((f) => (texto === f.sku ? f : conSku(f, texto)))}
                    onElegir={(s) => setForm((f) => (s.skuInterno === f.sku ? f : conSku(f, s.skuInterno)))}
                    buscar={buscarSkus}
                  />
                </div>
                {coloresActivos.length > 0 && (
                  <label className="flex items-center gap-2 text-sm pb-2">
                    <input type="checkbox" aria-label="Contar por color" checked={form.porColor}
                      onChange={(e) => setForm((f) => ({ ...f, porColor: e.target.checked }))} />
                    Contar por color
                  </label>
                )}
                {cargandoColores ? (
                  <p className="text-sm text-slate-500 pb-2">Cargando colores…</p>
                ) : form.porColor && coloresActivos.length > 0 ? (
                  <div className="w-full rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="flex flex-wrap gap-3">
                      {coloresActivos.map((v) => (
                        <label key={v.id} className="text-sm">
                          <span className="block mb-1">{v.nombre}</span>
                          <input aria-label={v.nombre} type="number" min="0" step="any" inputMode="decimal" className="w-28 px-2 py-1.5 border rounded-lg"
                            value={form.colores[v.id] ?? ''} onChange={(e) => setForm((f) => ({ ...f, colores: { ...f.colores, [v.id]: e.target.value } }))} />
                        </label>
                      ))}
                      <label className="text-sm">
                        <span className="block mb-1" title="Unidades contadas que no se pudieron asignar a un color: quedan en Sin asignar">Sin identificar</span>
                        <input aria-label="Sin identificar" type="number" min="0" step="any" inputMode="decimal" className="w-28 px-2 py-1.5 border rounded-lg"
                          value={form.sinIdentificar} onChange={(e) => setForm((f) => ({ ...f, sinIdentificar: e.target.value }))} />
                      </label>
                    </div>
                    <p className="text-xs text-slate-600">{`Total contado: ${cantidad(totalPorColor)}. Al confirmar, cada color queda en lo contado y el resto del SKU queda en Sin asignar: los colores que no cargues quedan en 0. Las unidades en cuarentena están en el depósito pero no son de ningún color: las unidades en cuarentena van en sin identificar.`}</p>
                    {avisoInactivos && <p role="status" className="text-xs text-amber-700">{avisoInactivos}</p>}
                  </div>
                ) : (
                <div className="w-40">
                  <label htmlFor={`${idSku}-cantidad`} className="block text-sm font-medium mb-1">Cantidad contada<Obligatorio /></label>
                  <input
                    id={`${idSku}-cantidad`}
                    aria-label="Cantidad contada"
                    type="number"
                    required
                    min="0"
                    step="any"
                    inputMode="decimal"
                    className="w-full px-3 py-2 border rounded-lg"
                    value={form.cantidad}
                    onChange={(e) => setForm((f) => ({ ...f, cantidad: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void agregar(); } }}
                  />
                </div>
                )}
                <Button onClick={() => void agregar()} loading={ocupado}>Agregar</Button>
              </div>
              <MenuImportar ocupado={importando} opciones={[
                { etiqueta: 'Planilla CSV/XLSX', descripcion: 'Columnas SKU y Cantidad; si el SKU ya está, se corrige', archivo: { accept: '.csv,.xlsx', onArchivo: (a) => void importar(a) } },
                { etiqueta: 'Descargar plantilla', descripcion: 'Planilla vacía para completar el conteo', onClick: () => window.open(`${URL_CONTEOS}/plantilla?format=xlsx`, '_blank') },
              ]} />
            </div>
          )}

          {erroresImportacion && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <p className="font-medium">La planilla tiene {erroresImportacion.length} {erroresImportacion.length === 1 ? 'error' : 'errores'}: no se importó nada. Corregila y volvé a importarla.</p>
              <ul className="mt-1 list-disc pl-5 max-h-40 overflow-y-auto">
                {erroresImportacion.map((e) => (
                  <li key={`${e.fila}-${e.sku ?? ''}`}>{`Fila ${e.fila}${e.sku ? ` · ${e.sku}` : ''}: ${e.mensaje}`}</li>
                ))}
              </ul>
            </div>
          )}

          <section aria-label="Resumen" className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
            {visible && r ? (
              <p>
                Líneas: {r.lineas} · Se suman al stock: {r.positivas} SKU (+{cantidad(r.unidadesPositivas ?? 0)} u.) · Se restan del stock: {r.negativas} SKU (−{cantidad(r.unidadesNegativas ?? 0)} u.)
                {' '}· Sin diferencia: {r.sinDiferencia}
                {!!r.superadas && <span className="text-amber-700"> · Superadas por un conteo posterior: {r.superadas}</span>}
                {detalle.estado === 'borrador' && !!r.enNegativo && <span className="text-red-700"> · {r.enNegativo} quedarían en negativo (se vendió más de lo contado)</span>}
              </p>
            ) : (
              <p>Líneas: {r?.lineas ?? 0}. Conteo ciego: lo que dice el sistema y las diferencias se ven al confirmarlo.</p>
            )}
          </section>

          {/* Muchas columnas: la tabla se desplaza a lo ancho dentro del modal y la de acciones queda fija a la derecha. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-1 pr-3 font-medium">SKU</th>
                  <th className="py-1 pr-3 font-medium">Descripción</th>
                  <th className="py-1 pr-3 font-medium text-right">Contado</th>
                  {visible && <th className="py-1 pr-3 font-medium text-right" title="Stock de la ubicación a la hora del conteo (Contado el, o la hora de carga de la línea)">Sistema</th>}
                  {visible && <th className="py-1 pr-3 font-medium text-right" title="Lo contado menos lo que decía el sistema a la hora del conteo. Si es positivo, había más mercadería de la que figuraba y al confirmar se suma al stock; si es negativo, se resta.">Diferencia con el sistema</th>}
                  {visible && conAjuste && <th className="py-1 pr-3 font-medium text-right">Ajustado</th>}
                  {visible && (
                    <th className="py-1 pr-3 font-medium" title="Lo que entró o salió de la ubicación después de la hora del conteo (neto y cantidad de movimientos). Sigue aplicado: no se cuenta de nuevo">
                      Movimientos desde el conteo
                    </th>
                  )}
                  {visible && <th className="py-1 pr-3 font-medium text-right" title="Reservado por ventas, en todo el depósito">{ETIQUETAS_STOCK.reservado}</th>}
                  {visible && <th className="py-1 pr-3 font-medium text-right" title="Total del depósito − reservado">{ETIQUETAS_STOCK.disponible}</th>}
                  <th className="py-1 pr-3 font-medium">Contado por</th>
                  {(editable || puedeExcluir) && <th className={`py-1 ${COLUMNA_ACCIONES}`}><span className="sr-only">Acciones</span></th>}
                </tr>
              </thead>
              <tbody>
                {detalle.lineas.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100">
                    <td className="py-1 pr-3 font-mono">{l.sku}</td>
                    <td className="py-1 pr-3">
                      {l.descripcion ?? '—'}
                      {l.superadaPor && (
                        <span className="block text-xs text-amber-700" title={`${l.superadaPor} es más nuevo: ya dejó bien el stock y manda el conteo más nuevo, así que esta línea no se ajusta`}>
                          Superada por un conteo posterior ({l.superadaPor})
                        </span>
                      )}
                      {!!l.variantes?.length && (
                        <ul className="mt-1 text-xs text-slate-600">
                          {l.variantes.map((v) => (
                            <li key={v.varianteId}>{`${v.nombre}: ${cantidad(v.contado)}${visible && v.sistema != null ? ` (sistema ${cantidad(v.sistema)})` : ''}`}</li>
                          ))}
                          {!!l.sinIdentificar && <li>{`Sin identificar: ${cantidad(l.sinIdentificar)}`}</li>}
                        </ul>
                      )}
                      {l.enCuarentena && detalle.estado === 'borrador' && (
                        <span className="block text-xs text-amber-700">
                          {`Tiene unidades en Cuarentena${l.cuarentena != null ? ` (${cantidad(l.cuarentena)})` : ''}: contalas también (están en el depósito).`}
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-3 text-right">
                      {editando?.id === l.id ? (
                        <input
                          aria-label={`Nueva cantidad de ${l.sku}`}
                          type="number" min="0" step="any" inputMode="decimal"
                          className="w-24 px-2 py-1 border rounded text-right"
                          value={editando.cantidad}
                          onChange={(e) => setEditando({ id: l.id, cantidad: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); void guardarEdicion(); }
                            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setEditando(null); }
                          }}
                        />
                      ) : cantidad(l.contado)}
                    </td>
                    {visible && <td className="py-1 pr-3 text-right">{l.stockSistema === null ? '—' : cantidad(l.stockSistema)}</td>}
                    {visible && <td className="py-1 pr-3 text-right">{l.diferencia === null ? '—' : <Diferencia valor={l.diferencia} />}</td>}
                    {visible && conAjuste && (
                      <td className="py-1 pr-3 text-right">
                        {l.ajusteAplicado !== null ? cantidad(l.ajusteAplicado) : confirmado ? '—' : <span className="text-amber-700">Pendiente</span>}
                      </td>
                    )}
                    {visible && <td className="py-1 pr-3"><MovimientosDesdeConteo m={l.movimientosPosteriores} /></td>}
                    {visible && <td className="py-1 pr-3 text-right">{formatoStock(l.reservado)}</td>}
                    {visible && <td className="py-1 pr-3 text-right">{formatoStock(l.disponible)}</td>}
                    <td className="py-1 pr-3">{l.contadoPor ?? '—'}</td>
                    {editable && (
                      <td className={`py-1 text-right whitespace-nowrap ${COLUMNA_ACCIONES}`}>
                        {editando?.id === l.id ? (
                          <>
                            <Button size="sm" onClick={() => void guardarEdicion()} loading={ocupado}>Guardar</Button>
                            <Button size="sm" variant="ghost" className="ml-1" onClick={() => setEditando(null)}>Cancelar</Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" aria-label={`Corregir ${l.sku}`} onClick={() => (l.variantes?.length ? cargarEnFormulario(l) : setEditando({ id: l.id, cantidad: String(l.contado) }))}>Corregir</Button>
                            <Button size="sm" variant="ghost" className="ml-1" aria-label={`Quitar ${l.sku}`} onClick={() => setPreguntar({ quitar: l })}>Quitar</Button>
                          </>
                        )}
                      </td>
                    )}
                    {puedeExcluir && (
                      <td className={`py-1 text-right whitespace-nowrap ${COLUMNA_ACCIONES}`}>
                        {l.ajusteAplicado === null && (
                          <Button size="sm" variant="ghost" aria-label={`Excluir renglón de ${l.sku}`} onClick={() => setPreguntar({ excluir: l })} disabled={ocupado}>
                            Excluir renglón
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {detalle.lineas.length === 0 && <p className="text-sm text-slate-500 py-2">Todavía no hay nada contado.</p>}
          </div>

          {puedeEmpezarSync && (
            <div className="flex flex-wrap items-center justify-end gap-3 pt-3 border-t border-slate-200">
              <span className="text-xs text-slate-500">Los SKU contados pueden empezar a sincronizar su stock con Mercado Libre.</span>
              <Button onClick={() => setSincronizando(true)}>Empezar a sincronizar estos SKU</Button>
            </div>
          )}

          {((detalle.estado === 'borrador' && (puedeCargar || esResponsable)) || (detalle.estado === 'confirmando' && esResponsable)) && (
            <div className="flex flex-wrap justify-end gap-3 pt-3 border-t border-slate-200">
              {puedeCargar && detalle.estado === 'borrador' && <Button variant="ghost" onClick={() => setPreguntar('anular')} disabled={ocupado}>Anular</Button>}
              {esResponsable && (
                <Button
                  onClick={() => setPreguntar('confirmar')}
                  disabled={ocupado || detalle.lineas.length === 0}
                  title={detalle.lineas.length === 0 ? 'Cargá lo contado antes de confirmar' : undefined}
                >
                  Confirmar conteo
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {sincronizando && <SincronizarSkusConteo conteoId={id} skus={skusDelConteo} onClose={() => setSincronizando(false)} />}
      <Confirm
        open={preguntar === 'confirmar'}
        title={`Confirmar el conteo #${id}`}
        message={mensajeConfirmar}
        confirmLabel="Confirmar"
        onConfirm={() => void responder()}
        onCancel={() => setPreguntar(null)}
      />
      <Confirm
        open={preguntar === 'anular'}
        title={`Anular el conteo #${id}`}
        message="El conteo queda anulado y no se puede volver a usar. No cambia el stock."
        confirmLabel="Anular"
        danger
        onConfirm={() => void responder()}
        onCancel={() => setPreguntar(null)}
      />
      <Confirm
        open={typeof preguntar === 'object' && preguntar !== null && 'quitar' in preguntar}
        title="Quitar la línea"
        message={typeof preguntar === 'object' && preguntar !== null && 'quitar' in preguntar ? `Se quita lo contado de ${preguntar.quitar.sku}.` : ''}
        confirmLabel="Quitar"
        danger
        onConfirm={() => void responder()}
        onCancel={() => setPreguntar(null)}
      />
      <Confirm
        open={typeof preguntar === 'object' && preguntar !== null && 'excluir' in preguntar}
        title="Excluir el renglón"
        message={typeof preguntar === 'object' && preguntar !== null && 'excluir' in preguntar
          ? `${preguntar.excluir.sku}: lo contado (${cantidad(preguntar.excluir.contado)}) no se ajusta y el renglón sale del conteo (queda anotado en las notas).\n\nUsalo cuando ese SKU no se puede ajustar; después volvé a confirmar el conteo para terminarlo. Los renglones ya ajustados no cambian.`
          : ''}
        confirmLabel="Excluir"
        danger
        onConfirm={() => void responder()}
        onCancel={() => setPreguntar(null)}
      />
    </Modal>
  );
}
